import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * UnifiedMyTable — default export is the standalone route wrapper
 * (/unified/my-table with AppShell), named export MyTablePanel is the
 * dashboard-embedded panel with no shell. Both render the same body.
 *
 * Replaces the recovery MyTableScreen. Three surfaces: Connections,
 * Invites (in + out), Discovery (suggested folks with overlapping dietary
 * context). Actions link out to /unified/table-questions and
 * /unified/safety-signals so each feature has one canonical home.
 *
 * Data comes from the /api/table/* endpoints (connections, invites,
 * discovery). The backend identifies the user from the Supabase token.
 * Nothing here is invented: fields the backend doesn't track are omitted.
 */

const EASE = [0.22, 1, 0.36, 1];

// Backend rows -> the shapes the cards render.
const toConnection = (c) => ({
  id: c.connection_id,
  name: c.display_name || c.name || 'Table member',
  reason: c.invited_reason,
  helpCount: c.help_count || 0,
  since: c.created_at,
});
const toInvite = (i) => ({
  id: i.invite_id,
  direction: i.direction,
  name: i.other_user?.name || 'A diner',
  message: i.invited_reason,
  created: i.created_at,
});
const toSuggestion = (h) => ({
  id: h.user_id,
  name: h.display_name || 'A diner',
  helped: h.interaction_count || 0,
});

function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (!Number.isFinite(mins)) return '';
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return then.toLocaleDateString();
}

const TABS = [
  { id: 'connections', label: 'Connections', icon: 'people' },
  { id: 'invites',     label: 'Invites',     icon: 'mail' },
  { id: 'discovery',   label: 'Discovery',   icon: 'compass' },
];

const AVATAR_HUES = [
  { bg: 'linear-gradient(160deg, #1E9BFF, #0F7FE8)' },
  { bg: 'linear-gradient(160deg, #FF6B8A, #E91E63)' },
  { bg: 'linear-gradient(160deg, #7C3AED, #5B21B6)' },
  { bg: 'linear-gradient(160deg, #F59E0B, #D97706)' },
  { bg: 'linear-gradient(160deg, #10B981, #059669)' },
  { bg: 'linear-gradient(160deg, #EC4899, #BE185D)' },
];

function hueFor(name) {
  if (!name) return AVATAR_HUES[0];
  const sum = Array.from(name).reduce((s, c) => s + c.charCodeAt(0), 0);
  return AVATAR_HUES[sum % AVATAR_HUES.length];
}

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

// --- Standalone route ---
export default function UnifiedMyTable() {
  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '28px 20px 4px', position: 'relative', zIndex: 1 }}>
        <motion.section
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, ease: EASE }}
          style={{ textAlign: 'center', marginBottom: 20 }}
        >
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 12 }}>
            <PeopleIcon size={12} /> MY TABLE
          </div>
          <h1 style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-0.025em', color: 'var(--cwm-ink)', margin: '0 0 8px' }}>
            Build your <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>dining circle</em>.
          </h1>
          <p style={{ maxWidth: 560, margin: '0 auto', fontSize: 15, fontWeight: 500, color: 'var(--cwm-gray-700)' }}>
            Connect with diners who share your allergies, preferences, or favorite spots. Safer together.
          </p>
        </motion.section>
      </div>
      <MyTablePanel userId={Number(localStorage.getItem('userId')) || null} />
    </AppShell>
  );
}

// --- Dashboard-embedded panel ---
export function MyTablePanel({ userId }) {
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [invitesIn,   setInvitesIn]   = useState([]);
  const [invitesOut,  setInvitesOut]  = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [activeTab, setActiveTab] = useState('connections');
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteTarget, setInviteTarget] = useState(null);
  const [alert, setAlert] = useState(null);

  const load = useCallback(async () => {
    const [conns, invs, sugs] = await Promise.allSettled([
      dataService.getMyTableConnections(),
      dataService.getMyTableInvites(),
      dataService.getMyTableSuggestions(),
    ]);
    const ok = (r) => (r.status === 'fulfilled' ? r.value : []);
    setConnections(ok(conns).map(toConnection));
    const invList = ok(invs).map(toInvite);
    setInvitesIn(invList.filter((i) => i.direction === 'incoming'));
    setInvitesOut(invList.filter((i) => i.direction === 'outgoing'));
    setSuggestions(ok(sugs).map(toSuggestion));
    if ([conns, invs, sugs].some((r) => r.status === 'rejected')) {
      setLoadError("Couldn't load everything from your table. Pull to refresh or try again shortly.");
    } else {
      setLoadError(null);
    }
  }, []);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    let cancelled = false;
    load().finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId, load]);

  const flash = (tone, text) => {
    setAlert({ tone, text, id: Date.now() });
    setTimeout(() => setAlert(null), 3000);
  };

  const stats = useMemo(() => ({
    connections: connections.length,
    pending: invitesIn.length + invitesOut.length,
  }), [connections, invitesIn, invitesOut]);

  // `target` is set when inviting someone from Discovery (we know their id).
  const sendInvite = async ({ email, reason, target }) => {
    try {
      await dataService.sendMyTableInvite({ email, userId: target?.id, reason });
      setInviteModalOpen(false);
      setInviteTarget(null);
      flash('green', `Invite sent to ${target?.name || email}.`);
      await load();
    } catch (e) {
      throw e; // the modal shows the server's message
    }
  };

  const acceptInvite = async (inv) => {
    try {
      await dataService.acceptMyTableInvite(inv.id);
      flash('green', `Connected with ${inv.name}.`);
      await load();
    } catch (e) {
      flash('red', e.message || "Couldn't accept that invite.");
    }
  };

  const declineInvite = async (inv) => {
    try {
      await dataService.declineMyTableInvite(inv.id);
      flash('red', `Declined invite from ${inv.name}.`);
      await load();
    } catch (e) {
      flash('red', e.message || "Couldn't decline that invite.");
    }
  };

  const removeConnection = async (conn) => {
    if (!window.confirm(`Remove ${conn.name} from your table?`)) return;
    try {
      await dataService.removeMyTableConnection(conn.id);
      flash('red', `${conn.name} removed.`);
      await load();
    } catch (e) {
      flash('red', e.message || "Couldn't remove that connection.");
    }
  };

  const inviteSuggestion = (sug) => {
    setInviteTarget(sug);
    setInviteModalOpen(true);
  };

  if (loading) return <LoadingBlock />;

  return (
    <div style={{ padding: '18px 20px' }}>

      <AnimatePresence>
        {alert && (
          <motion.div
            key={alert.id}
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            style={{
              padding: '10px 14px', borderRadius: 14, marginBottom: 14,
              background: alert.tone === 'red' ? 'var(--cwm-red-light)' : 'var(--cwm-green-light)',
              color: alert.tone === 'red' ? 'var(--cwm-red-text)' : 'var(--cwm-green-text)',
              fontSize: 13, fontWeight: 800,
            }}
          >{alert.text}</motion.div>
        )}
      </AnimatePresence>

      {loadError && (
        <div style={{
          padding: '10px 14px', borderRadius: 14, marginBottom: 14,
          background: 'var(--cwm-amber-light)', color: 'var(--cwm-amber-text)',
          fontSize: 13, fontWeight: 800,
        }}>{loadError}</div>
      )}

      {/* Purple hero stats strip */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.46, ease: EASE }}
        style={{
          position: 'relative', overflow: 'hidden',
          padding: '22px 24px', borderRadius: 24,
          background: 'linear-gradient(160deg, #7C3AED 0%, #5B21B6 60%, #3B1E80 100%)',
          color: '#fff', marginBottom: 18,
          boxShadow: '0 20px 44px rgba(124,58,237,0.32)',
        }}
      >
        <div aria-hidden style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(255,255,255,0.18) 0, rgba(255,255,255,0.18) 1.5px, transparent 1.6px)',
          backgroundSize: '22px 22px', opacity: 0.4,
        }}/>
        <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.82 }}>
              MY TABLE
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: '-0.015em', marginTop: 4 }}>
              {stats.connections === 0 ? 'Start building your table' : `${stats.connections} people in your circle`}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.9, marginTop: 4, maxWidth: 440 }}>
              Connect with other diners who share allergies, preferences, or favorite spots. You're safer together.
            </div>
          </div>
          <div style={{
            width: 56, height: 56, borderRadius: 16,
            background: 'rgba(255,255,255,0.18)',
            display: 'grid', placeItems: 'center',
            backdropFilter: 'blur(6px)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.22)',
          }}>
            <PeopleIcon size={24} />
          </div>
        </div>
        <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginTop: 18 }}>
          <StatTile label="Connections"     value={stats.connections} />
          <StatTile label="Pending invites" value={stats.pending} />
        </div>
      </motion.div>

      {/* Action pills */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.46, delay: 0.08, ease: EASE }}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 22 }}
      >
        <ActionPill tone="blue"   icon="plus"   label="Send invite"          onClick={() => { setInviteTarget(null); setInviteModalOpen(true); }} />
        <ActionPill tone="green"  icon="chat"   label="Ask the table"        onClick={() => navigate('/unified/table-questions')} />
        <ActionPill tone="orange" icon="shield" label="Share safety signal"  onClick={() => navigate('/unified/safety-signals')} />
      </motion.div>

      {/* Sub-tabs */}
      <div style={{
        display: 'flex', gap: 6, marginBottom: 14, padding: 4,
        background: 'var(--cwm-gray-50)', borderRadius: 999, border: '1px solid var(--cwm-gray-200)',
      }}>
        {TABS.map((t) => {
          const on = activeTab === t.id;
          const count =
            t.id === 'connections' ? connections.length :
            t.id === 'invites'     ? invitesIn.length + invitesOut.length :
            suggestions.length;
          return (
            <button key={t.id} type="button" onClick={() => setActiveTab(t.id)}
                    style={{
                      flex: 1,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      padding: '10px 14px', borderRadius: 999,
                      background: on ? '#fff' : 'transparent',
                      color: on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-700)',
                      border: 'none', cursor: 'pointer',
                      fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 900,
                      boxShadow: on ? '0 4px 12px rgba(10,37,64,0.08)' : 'none',
                      transition: 'all 180ms ease',
                    }}>
              <TabIcon name={t.icon} size={14} /> {t.label}
              <span style={{
                padding: '1px 8px', borderRadius: 999,
                background: on ? 'var(--cwm-blue-pale)' : 'var(--cwm-gray-200)',
                color: on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-500)',
                fontSize: 11, fontWeight: 900,
              }}>{count}</span>
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.26, ease: EASE }}
        >
          {activeTab === 'connections' && (
            <ConnectionsTab connections={connections} onRemove={removeConnection} onInvite={() => setInviteModalOpen(true)} />
          )}
          {activeTab === 'invites' && (
            <InvitesTab invitesIn={invitesIn} invitesOut={invitesOut}
                        onAccept={acceptInvite} onDecline={declineInvite}
                        onSend={() => setInviteModalOpen(true)} />
          )}
          {activeTab === 'discovery' && (
            <DiscoveryTab suggestions={suggestions} onInvite={inviteSuggestion} />
          )}
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        {inviteModalOpen && (
          <InviteModal target={inviteTarget}
                       onCancel={() => { setInviteModalOpen(false); setInviteTarget(null); }}
                       onSend={sendInvite} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============ TABS ============

function ConnectionsTab({ connections, onRemove, onInvite }) {
  if (!connections.length) {
    return <EmptyTab
      icon="people"
      title="No connections yet"
      body="Start building your table by inviting friends and family who also dine with allergies or preferences."
      cta="Send your first invite" onAction={onInvite}
    />;
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
      {connections.map((c, i) => (
        <ConnectionCard key={c.id} conn={c} index={i} onRemove={() => onRemove(c)} />
      ))}
    </div>
  );
}

function InvitesTab({ invitesIn, invitesOut, onAccept, onDecline, onSend }) {
  if (!invitesIn.length && !invitesOut.length) {
    return <EmptyTab
      icon="mail"
      title="No pending invites"
      body="When someone invites you or you invite someone, it'll show up here until they respond."
      cta="Send an invite" onAction={onSend}
    />;
  }
  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {invitesIn.length > 0 && (
        <section>
          <div className="cwm-eyebrow" style={{ marginBottom: 8 }}>INCOMING ({invitesIn.length})</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {invitesIn.map((inv, i) => (
              <IncomingInviteCard key={inv.id} invite={inv} index={i}
                                  onAccept={() => onAccept(inv)} onDecline={() => onDecline(inv)} />
            ))}
          </div>
        </section>
      )}
      {invitesOut.length > 0 && (
        <section>
          <div className="cwm-eyebrow" style={{ marginBottom: 8 }}>SENT ({invitesOut.length})</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {invitesOut.map((inv, i) => (
              <OutgoingInviteCard key={inv.id} invite={inv} index={i} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function DiscoveryTab({ suggestions, onInvite }) {
  if (!suggestions.length) {
    return <EmptyTab
      icon="compass"
      title="No suggestions right now"
      body="We suggest diners with overlapping dietary needs nearby. Check back after you share a few reviews."
    />;
  }
  return (
    <div>
      <div className="cwm-eyebrow" style={{ marginBottom: 10 }}>
        DINERS LIKE YOU ({suggestions.length})
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {suggestions.map((s, i) => (
          <SuggestionCard key={s.id} sug={s} index={i} onInvite={() => onInvite(s)} />
        ))}
      </div>
    </div>
  );
}

// ============ CARDS ============

function ConnectionCard({ conn, index, onRemove }) {
  const hue = hueFor(conn.name);
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, delay: index * 0.04, ease: EASE }}
      whileHover={{ y: -2 }}
      className="cwm-sheet"
      style={{ padding: 18 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: hue.bg, color: '#fff',
          display: 'grid', placeItems: 'center',
          fontSize: 16, fontWeight: 900, letterSpacing: '-0.02em',
          boxShadow: '0 6px 14px rgba(13,71,161,0.18)', flexShrink: 0,
        }}>
          {initials(conn.name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.005em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {conn.name}
          </div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2 }}>
            {conn.helpCount > 0
              ? `Helped ${conn.helpCount} time${conn.helpCount === 1 ? '' : 's'}`
              : `Connected ${timeAgo(conn.since)}`}
          </div>
        </div>
        <button type="button" onClick={onRemove} aria-label="remove" style={iconBtn}>
          <TrashIcon size={13} />
        </button>
      </div>
      {conn.reason && (
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--cwm-gray-700)', fontStyle: 'italic', lineHeight: 1.45 }}>
          “{conn.reason}”
        </div>
      )}
    </motion.article>
  );
}

function IncomingInviteCard({ invite, index, onAccept, onDecline }) {
  const hue = hueFor(invite.name);
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.04, ease: EASE }}
      className="cwm-sheet"
      style={{ padding: 18 }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 12,
          background: hue.bg, color: '#fff',
          display: 'grid', placeItems: 'center',
          fontSize: 14, fontWeight: 900, flexShrink: 0,
        }}>
          {initials(invite.name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 900, color: 'var(--cwm-ink)' }}>
            {invite.name} wants to connect
          </div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2 }}>
            {timeAgo(invite.created)}
          </div>
        </div>
      </div>
      {invite.message && (
        <p style={{
          margin: '0 0 12px', padding: '10px 14px',
          background: 'var(--cwm-gray-50)',
          borderLeft: '3px solid var(--cwm-blue)',
          borderRadius: '0 12px 12px 0',
          fontSize: 13, fontWeight: 500, color: 'var(--cwm-gray-700)', fontStyle: 'italic', lineHeight: 1.5,
        }}>"{invite.message}"</p>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={onAccept} className="cwm-btn" style={{ flex: 1, justifyContent: 'center', padding: '10px 14px' }}>
          <CheckIcon size={12} /> Accept
        </button>
        <button type="button" onClick={onDecline} className="cwm-btn-secondary" style={{ padding: '10px 14px' }}>
          Decline
        </button>
      </div>
    </motion.article>
  );
}

function OutgoingInviteCard({ invite, index }) {
  const hue = hueFor(invite.name);
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.04, ease: EASE }}
      className="cwm-sheet"
      style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 12,
        background: hue.bg, color: '#fff',
        display: 'grid', placeItems: 'center',
        fontSize: 13, fontWeight: 900, flexShrink: 0, opacity: 0.7,
      }}>
        {initials(invite.name)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--cwm-ink)' }}>
          Awaiting reply from {invite.name}
        </div>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2 }}>
          sent {timeAgo(invite.created)}
        </div>
      </div>
      <span style={{
        padding: '4px 10px', borderRadius: 999,
        background: 'var(--cwm-amber-light)', color: 'var(--cwm-amber-text)',
        fontSize: 10.5, fontWeight: 900, letterSpacing: '0.04em',
      }}>PENDING</span>
    </motion.article>
  );
}

function SuggestionCard({ sug, index, onInvite }) {
  const hue = hueFor(sug.name);
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, delay: index * 0.04, ease: EASE }}
      whileHover={{ y: -2 }}
      className="cwm-sheet"
      style={{ padding: 18 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: hue.bg, color: '#fff',
          display: 'grid', placeItems: 'center',
          fontSize: 15, fontWeight: 900, flexShrink: 0,
        }}>
          {initials(sug.name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.005em' }}>
            {sug.name}
          </div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2 }}>
            Helped you {sug.helped} time{sug.helped === 1 ? '' : 's'}
          </div>
        </div>
      </div>
      <button type="button" onClick={onInvite} className="cwm-btn"
              style={{ width: '100%', justifyContent: 'center', padding: '9px 14px', fontSize: 13 }}>
        <PlusIcon size={12} /> Invite to your table
      </button>
    </motion.article>
  );
}

// ============ DIALOG ============

// The backend requires a reason of at least 20 characters ("no random
// connects"). `target` is a Discovery suggestion, so no email is needed.
const MIN_REASON = 20;

function InviteModal({ target, onCancel, onSend }) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async () => {
    const e = email.trim();
    if (!target) {
      if (!e) { setError('Add their email first.'); return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { setError("That email doesn't look right."); return; }
    }
    const reason = message.trim();
    if (reason.length < MIN_REASON) {
      setError(`Tell them why you'd like them at your table (at least ${MIN_REASON} characters).`);
      return;
    }
    setError('');
    setSending(true);
    try {
      await onSend({ email: target ? undefined : e, reason, target });
    } catch (err) {
      setError(err?.message || "Couldn't send the invite. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(10,37,64,0.55)', backdropFilter: 'blur(6px)',
        display: 'grid', placeItems: 'center', padding: 20,
      }}
    >
      <motion.div
        initial={{ scale: 0.96, y: 10, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.96, y: 10, opacity: 0 }}
        transition={{ duration: 0.28, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 28, padding: 24, maxWidth: 460, width: '100%', boxShadow: 'var(--cwm-shadow-hero)' }}
      >
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <div className="cwm-eyebrow-badge" style={{ marginBottom: 8 }}>
              <MailIcon size={12} /> SEND INVITE
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 900, margin: 0, color: 'var(--cwm-ink)', letterSpacing: '-0.01em' }}>
              Grow your table
            </h2>
          </div>
          <button onClick={onCancel} aria-label="close"
                  style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--cwm-gray-100)', border: 'none', cursor: 'pointer', fontSize: 18, fontWeight: 900, color: 'var(--cwm-gray-700)' }}>×</button>
        </header>

        {target ? (
          <div style={{ marginBottom: 12, fontSize: 14, fontWeight: 800, color: 'var(--cwm-ink)' }}>
            Inviting {target.name}
          </div>
        ) : (
        <div style={{ marginBottom: 12 }}>
          <div className="cwm-eyebrow" style={{ marginBottom: 6, color: 'var(--cwm-gray-700)' }}>EMAIL OF THEIR CHAT WITH MENU ACCOUNT</div>
          <input value={email} onChange={(e) => setEmail(e.target.value)}
                 placeholder="their@email.com" type="email"
                 style={{
                   width: '100%', boxSizing: 'border-box',
                   padding: '12px 16px', borderRadius: 14,
                   border: '1.5px solid var(--cwm-blue-light)',
                   background: '#F7FBFF', color: 'var(--cwm-ink)',
                   fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 600,
                   outline: 'none',
                 }}/>
        </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <div className="cwm-eyebrow" style={{ marginBottom: 6, color: 'var(--cwm-gray-700)' }}>WHY THEM? (SHOWN WITH YOUR INVITE)</div>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                    placeholder="Hey! Want to share restaurant picks? I trust your gluten-free radar..." rows={3}
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      padding: '12px 16px', borderRadius: 14,
                      border: '1.5px solid var(--cwm-blue-light)',
                      background: '#F7FBFF', color: 'var(--cwm-ink)',
                      fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 500, lineHeight: 1.5,
                      outline: 'none', resize: 'vertical',
                    }}/>
        </div>

        {error && <div style={{ fontSize: 13, color: 'var(--cwm-red-text)', fontWeight: 700, marginBottom: 10 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="cwm-btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="cwm-btn" onClick={submit} disabled={sending}>
            <MailIcon size={12} /> {sending ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============ SHARED ============

function StatTile({ label, value }) {
  return (
    <div style={{
      padding: '12px 14px', borderRadius: 14,
      background: 'rgba(255,255,255,0.18)',
      backdropFilter: 'blur(6px)',
      border: '1px solid rgba(255,255,255,0.22)', color: '#fff',
    }}>
      <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.8 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 900, letterSpacing: '-0.02em', marginTop: 2 }}>{value}</div>
    </div>
  );
}

function ActionPill({ tone, icon, label, onClick }) {
  const tones = {
    blue:   { bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  solid: 'var(--cwm-blue-deep)' },
    green:  { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', solid: 'var(--cwm-green)' },
    orange: { bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)', solid: 'var(--cwm-amber)' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <motion.button
      type="button" onClick={onClick}
      whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 10,
        padding: '14px 18px', borderRadius: 18,
        background: '#fff', color: t.fg,
        border: `1.5px solid ${t.solid}22`,
        fontFamily: 'var(--cwm-font)', fontSize: 13.5, fontWeight: 900,
        cursor: 'pointer',
        boxShadow: `0 6px 14px ${t.solid}11`,
        transition: 'all 160ms ease',
      }}>
      <span style={{
        width: 34, height: 34, borderRadius: 10,
        background: t.bg, color: t.fg,
        display: 'grid', placeItems: 'center',
      }}>
        <ActionIcon name={icon} size={16} />
      </span>
      {label}
    </motion.button>
  );
}

function EmptyTab({ icon, title, body, cta, onAction }) {
  return (
    <div style={{
      padding: '44px 24px', textAlign: 'center',
      borderRadius: 22, background: 'var(--cwm-gray-50)',
      border: '1.5px dashed var(--cwm-gray-300)',
    }}>
      <div style={{
        width: 60, height: 60, margin: '0 auto 14px', borderRadius: '50%',
        background: '#fff', color: 'var(--cwm-blue-deep)',
        display: 'grid', placeItems: 'center',
        border: '1.5px dashed var(--cwm-blue-light)',
      }}>
        <TabIcon name={icon} size={24} />
      </div>
      <h3 style={{ fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)', margin: '0 0 6px', letterSpacing: '-0.005em' }}>
        {title}
      </h3>
      <p style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--cwm-gray-700)', maxWidth: 400, margin: '0 auto 18px', lineHeight: 1.5 }}>
        {body}
      </p>
      {cta && (
        <button type="button" onClick={onAction} className="cwm-btn">
          <PlusIcon size={12} /> {cta}
        </button>
      )}
    </div>
  );
}

function LoadingBlock() {
  return (
    <div style={{ padding: '60px 20px', textAlign: 'center' }}>
      <div style={{ width: 40, height: 40, margin: '0 auto 12px', border: '4px solid var(--cwm-blue-light)', borderTopColor: 'var(--cwm-blue-deep)', borderRadius: '50%', animation: 'cwmSpin 900ms linear infinite' }}/>
      <style>{`@keyframes cwmSpin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>Loading your table…</div>
    </div>
  );
}

// ============ STYLES + ICONS ============

const iconBtn = {
  width: 30, height: 30, borderRadius: 999,
  background: '#fff', color: 'var(--cwm-gray-500)',
  border: '1px solid var(--cwm-gray-200)',
  cursor: 'pointer', display: 'grid', placeItems: 'center', flexShrink: 0,
};

function PeopleIcon({ size = 20 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11a3 3 0 100-6 3 3 0 000 6zM17 12a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM3 19c0-3 3-5 6-5s6 2 6 5M16 19c0-2 1.5-4 4-4"/></svg>; }
function PlusIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>; }
function CheckIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>; }
function TrashIcon({ size = 13 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>; }
function MailIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></svg>; }
function TabIcon({ name, size = 14 }) {
  const paths = {
    people:  'M9 11a3 3 0 100-6 3 3 0 000 6zM17 12a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM3 19c0-3 3-5 6-5s6 2 6 5M16 19c0-2 1.5-4 4-4',
    mail:    'M3 5h18v14H3zM3 7l9 6 9-6',
    compass: 'M12 3a9 9 0 100 18 9 9 0 000-18zM16 8l-2 6-6 2 2-6z',
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d={paths[name] || paths.people}/></svg>;
}
function ActionIcon({ name, size = 16 }) {
  const paths = {
    plus:   'M12 5v14M5 12h14',
    chat:   'M21 12a9 9 0 01-4.5 7.8L17 22l-4-2a9 9 0 119-8z',
    shield: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d={paths[name] || paths.plus}/></svg>;
}
